import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Clock, Award, Download, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OreVolontari() {
  const navigate = useNavigate();
  const [oreVolontari, setOreVolontari] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ore-volontari`, { credentials: 'include' });
      if (response.ok) {
        setOreVolontari(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificaTuttiBadge = async () => {
    let badgesAssegnati = 0;
    for (const vol of oreVolontari) {
      try {
        const response = await fetch(`${API_URL}/api/badges/verifica/${vol.volontario_id}`, {
          method: 'POST',
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          badgesAssegnati += (data.nuovi_badges?.length || 0);
        }
      } catch (e) {}
    }
    if (badgesAssegnati > 0) {
      toast.success(`Assegnati ${badgesAssegnati} nuovi badge!`);
      fetchData();
    } else {
      toast.info('Nessun nuovo badge da assegnare');
    }
  };

  const totaleOre = oreVolontari.reduce((sum, v) => sum + v.ore_totali, 0);
  const totaleOreAnno = oreVolontari.reduce((sum, v) => sum + v.ore_anno_corrente, 0);
  const totaleBadges = oreVolontari.reduce((sum, v) => sum + (v.badges?.length || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ore-volontari-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Ore Volontari</h1>
          <p className="page-subtitle">Conteggio ore e riconoscimenti volontari</p>
        </div>
        <Button onClick={handleVerificaTuttiBadge}>
          <Award className="w-4 h-4 mr-2" />
          Verifica Badge per Tutti
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#0055BB]/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-[#0055BB]" />
            </div>
            <div>
              <p className="text-2xl font-bold">{oreVolontari.length}</p>
              <p className="text-sm text-slate-500">Volontari Attivi</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#008A55]/10 flex items-center justify-center">
              <Clock className="w-6 h-6 text-[#008A55]" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totaleOre.toFixed(0)}</p>
              <p className="text-sm text-slate-500">Ore Totali</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totaleOreAnno.toFixed(0)}</p>
              <p className="text-sm text-slate-500">Ore Anno Corrente</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <Award className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totaleBadges}</p>
              <p className="text-sm text-slate-500">Badge Assegnati</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#0055BB]" />
            Classifica Ore Volontari
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {oreVolontari.length === 0 ? (
            <div className="empty-state py-16">
              <Clock className="empty-state-icon" />
              <p className="empty-state-title">Nessun dato disponibile</p>
              <p className="empty-state-text">Le ore verranno registrate con i check-in agli eventi</p>
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>Volontario</TableHead>
                  <TableHead className="text-right">Ore Totali</TableHead>
                  <TableHead className="text-right">Ore Anno</TableHead>
                  <TableHead className="text-right">Eventi</TableHead>
                  <TableHead>Badge</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {oreVolontari.map((vol, idx) => (
                  <TableRow 
                    key={vol.volontario_id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/volontari/${vol.volontario_id}`)}
                  >
                    <TableCell>
                      {idx === 0 && <span className="text-xl">🥇</span>}
                      {idx === 1 && <span className="text-xl">🥈</span>}
                      {idx === 2 && <span className="text-xl">🥉</span>}
                      {idx > 2 && <span className="text-slate-400">{idx + 1}</span>}
                    </TableCell>
                    <TableCell className="font-medium">{vol.nome}</TableCell>
                    <TableCell className="text-right font-semibold text-[#0055BB]">
                      {vol.ore_totali} h
                    </TableCell>
                    <TableCell className="text-right">
                      {vol.ore_anno_corrente} h
                    </TableCell>
                    <TableCell className="text-right text-slate-500">
                      {vol.num_eventi}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {vol.badges?.map((badge, bidx) => (
                          <Badge key={bidx} className="bg-amber-100 text-amber-800 text-xs">
                            {badge.nome?.replace('Volontario ', '')}
                          </Badge>
                        ))}
                        {(!vol.badges || vol.badges.length === 0) && (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Badge Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Legenda Badge</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
              <Award className="w-6 h-6 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Bronze</p>
                <p className="text-xs text-amber-600">50 ore</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
              <Award className="w-6 h-6 text-slate-500" />
              <div>
                <p className="font-medium text-slate-700">Silver</p>
                <p className="text-xs text-slate-500">100 ore</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg">
              <Award className="w-6 h-6 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">Gold</p>
                <p className="text-xs text-yellow-600">200 ore</p>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
              <Award className="w-6 h-6 text-purple-600" />
              <div>
                <p className="font-medium text-purple-800">Platinum</p>
                <p className="text-xs text-purple-600">500 ore</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
